import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Badge } from '../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableCell } from '../ui/table';
import { SortableHead } from './TableControls';
import GameTitleButton from './GameTitleButton';
import { metricFormatters } from './BenchmarkMultiple';
import { periodLabel, preReleasePeriod, releaseTypeMeta } from './releaseData';
import {
  ALL_PROVIDERS, CHECKPOINT_DAYS, lineSeries, providerCheckpointRows, providerPreReleaseOptions, sortProviderRows,
} from './launchCurves';

const AXIS_COLOR = '#7a8499';

// How many of the table's top rows (in its current order) the chart draws.
const LINE_LIMITS = [3, 5, 10];

function LinesTooltip({ active, payload, games, fmt }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  const rows = games
    .map(g => ({ ...g, value: point[g.key] }))
    .filter(g => g.value != null)
    .sort((a, b) => b.value - a.value);
  return (
    <div className="bg-surface-2 border border-white/10 rounded-md p-2.5 text-[11px] min-w-[220px]">
      <p className="font-bold text-ink mb-1">First {point.day} day{point.day === 1 ? '' : 's'}</p>
      {rows.map(g => (
        <div key={g.key} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
          <span className="text-ink-dim truncate max-w-[160px]">{g.curve.game_title}</span>
          <span className="text-ink ml-auto pl-2">{fmt(g.value)}</span>
        </div>
      ))}
    </div>
  );
}

function PeriodText({ curve }) {
  const period = preReleasePeriod(curve);
  return period ? `${periodLabel(period.start, period.end)} · ${period.days}d` : '—';
}

// One provider's pre-releases — or every provider's — on one chart: each line
// one game's own cumulative total by day since release, so lines only rise
// and a younger game's line is simply shorter. The table lists them all with
// their totals at the checkpoint days; the chart draws its top N rows in the
// table's current order (e.g. sort by day 14 to chart the best launches).
export default function ProviderPreReleases({ curves, metric, metricLabel, horizon, onOpenGame }) {
  const options = useMemo(() => {
    const perProvider = providerPreReleaseOptions(curves);
    const total = perProvider.reduce((s, o) => s + o.count, 0);
    return total ? [{ provider: ALL_PROVIDERS, count: total }, ...perProvider] : [];
  }, [curves]);
  const [provider, setProvider] = useState(null);
  const [lineLimit, setLineLimit] = useState(5);
  const [sortCol, setSortCol] = useState('release_date');
  const [sortDir, setSortDir] = useState('desc');
  const active = options.some(o => o.provider === provider) ? provider : options[0]?.provider;

  // Reset the pick when the brand changes and the provider disappears.
  useEffect(() => { if (provider && !options.some(o => o.provider === provider)) setProvider(null); }, [options, provider]);

  const { fmt, axisFmt } = metricFormatters(metric);
  const rows = useMemo(() => (active ? providerCheckpointRows(curves, active, metric) : []), [curves, active, metric]);
  const sortedRows = useMemo(() => sortProviderRows(rows, sortCol, sortDir), [rows, sortCol, sortDir]);
  const { games, data } = useMemo(
    () => lineSeries(sortedRows.slice(0, lineLimit).map(r => r.curve), metric, horizon),
    [sortedRows, lineLimit, metric, horizon],
  );
  const allProviders = active === ALL_PROVIDERS;

  function toggleSort(col) {
    if (sortCol === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }
  const sortProps = { sortCol, sortDir, onSort: toggleSort };
  const colorById = new Map(games.map(g => [g.curve.unique_game_id, g.color]));

  if (options.length === 0) {
    return <div className="flex items-center justify-center py-10 text-xs text-ink-dim">No pre-releases yet</div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <label className="text-[11px] text-ink-dim mb-1 block">Provider</label>
          <Select value={active} onValueChange={setProvider}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {options.map(o => (
                <SelectItem key={o.provider} value={o.provider}>
                  {o.provider === ALL_PROVIDERS ? 'All providers' : o.provider} ({o.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] text-ink-dim mb-1 block">Lines</label>
          <Select value={String(lineLimit)} onValueChange={v => setLineLimit(Number(v))}>
            <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LINE_LIMITS.map(n => <SelectItem key={n} value={String(n)}>Top {n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-[11px] text-ink-dim pb-2">
          {metricLabel}, cumulative by day since release — the first {games.length} of{' '}
          {rows.length === 1 ? '1 pre-release' : `${rows.length} pre-releases`} in the table&apos;s order below
        </p>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
        {games.map(g => (
          <span key={g.key} className="inline-flex items-center gap-1.5 text-[11px] text-ink-dim">
            <span className="w-4 border-t-2" style={{ borderColor: g.color }} />
            {g.curve.game_title}
          </span>
        ))}
      </div>
      <div className="h-[300px] mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="day" stroke={AXIS_COLOR} fontSize={11} tickFormatter={d => `D${d}`} />
            <YAxis tickFormatter={axisFmt} stroke={AXIS_COLOR} fontSize={11} width={56} />
            <Tooltip content={<LinesTooltip games={games} fmt={fmt} />} cursor={{ stroke: 'rgba(255,255,255,0.15)' }} />
            {games.map(g => (
              <Line key={g.key} dataKey={g.key} stroke={g.color} strokeWidth={2} dot={false} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="border border-white/10 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead col="game_title" label="Game" {...sortProps} />
              {allProviders && <SortableHead col="provider" label="Provider" {...sortProps} />}
              <SortableHead col="release_type" label="Type" {...sortProps} />
              <SortableHead col="release_date" label="Pre-release period" {...sortProps} />
              {CHECKPOINT_DAYS.map((d, i) => (
                <SortableHead key={d} col={`d${i}`} label={`${metricLabel} · day ${d}`} right {...sortProps} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.map(({ curve, values }) => {
              const type = releaseTypeMeta(curve.release_type);
              const color = colorById.get(curve.unique_game_id);
              return (
                <TableRow key={curve.unique_game_id} onClick={() => onOpenGame(curve.unique_game_id)} className="cursor-pointer">
                  <TableCell className="font-bold text-ink">
                    <span className="inline-flex items-center gap-2">
                      {color && <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />}
                      <GameTitleButton
                        title={curve.game_title}
                        onActivate={() => onOpenGame(curve.unique_game_id)}
                        label={`Compare ${curve.game_title} with its benchmarks`}
                      />
                    </span>
                  </TableCell>
                  {allProviders && <TableCell className="text-ink-dim">{curve.provider}</TableCell>}
                  <TableCell><Badge variant={type.variant} className="whitespace-nowrap">{type.label}</Badge></TableCell>
                  <TableCell className="whitespace-nowrap text-ink-dim"><PeriodText curve={curve} /></TableCell>
                  {values.map((v, i) => (
                    <TableCell key={CHECKPOINT_DAYS[i]} className="text-right">
                      {v == null ? <span className="text-[11px] text-ink-mute">not reached</span> : fmt(v)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="text-[11px] text-ink-dim mt-2">Click a game to see it against its benchmarks.</p>
    </div>
  );
}
